/**
 * Date-window helpers for time-based dimension members.
 * Kept out of Extensions.ts so fork-ts-checker on CI can still resolve them
 * when late exports on that large module are dropped under memory pressure.
 */

export function parseMemberDateBound(value: unknown, fallback: number): number {
    if (value == null || value === '')
        return fallback;
    if (value instanceof Date && !isNaN(value.getTime())) {
        return value.getFullYear() * 10000 + (value.getMonth() + 1) * 100 + value.getDate();
    }
    const text = String(value).trim();
    if (/^\d{8}$/.test(text)) {
        const parsed = Number.parseInt(text, 10);
        return Number.isNaN(parsed) ? fallback : parsed;
    }
    const millis = Date.parse(text);
    if (!Number.isNaN(millis)) {
        const parsed = new Date(millis);
        return parsed.getFullYear() * 10000 + (parsed.getMonth() + 1) * 100 + parsed.getDate();
    }
    return fallback;
}

export function extractMemberDateBounds(
    member: {
        startDate?: unknown;
        endDate?: unknown;
        properties?: { propertyName?: string | null; propertyValue?: string | null }[] | null;
    } | null | undefined,
    extra?: Record<string, unknown> | null,
): { start: unknown, end: unknown } {
    if (member == null && extra == null)
        return { start: undefined, end: undefined };
    const anyMember = (member ?? extra ?? {}) as Record<string, unknown>;
    let start = member?.startDate ?? anyMember.StartDate ?? extra?.StartDate ?? extra?.startDate;
    let end = member?.endDate ?? anyMember.EndDate ?? extra?.EndDate ?? extra?.endDate;
    if ((start == null || start === '' || end == null || end === '') && member?.properties) {
        for (let i = 0; i < member.properties.length; i++) {
            const name = (member.properties[i].propertyName || '').replace(/\s+/g, '').toLowerCase();
            if ((start == null || start === '') && name === 'startdate')
                start = member.properties[i].propertyValue;
            if ((end == null || end === '') && name === 'enddate')
                end = member.properties[i].propertyValue;
        }
    }
    return { start, end };
}

export function isMemberInEffectiveDateRange(startDate: unknown, endDate: unknown, effectiveDate: Date | null): boolean {
    if (effectiveDate == null)
        return true;
    const efd = effectiveDate.getFullYear() * 10000
        + (effectiveDate.getMonth() + 1) * 100
        + effectiveDate.getDate();
    const startBound = parseMemberDateBound(startDate ?? '19000101', 19000101);
    const endBound = parseMemberDateBound(endDate ?? '99990101', 99990101);
    return efd >= startBound && efd <= endBound;
}

export function parseYyyyMmDdToLocalDate(value: string | null | undefined): Date | null {
    if (value == null)
        return null;
    const text = String(value).trim();
    if (!/^\d{8}$/.test(text))
        return null;
    const year = text.slice(0, 4);
    const month = text.slice(4, 6);
    const day = text.slice(6, 8);
    let parsedDate = new Date(`${year}-${month}-${day}`);
    if (isNaN(parsedDate.getTime()))
        return null;
    const userTimezoneOffset = parsedDate.getTimezoneOffset() * 60000;
    parsedDate = new Date(parsedDate.getTime() + userTimezoneOffset);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
}

const periodMonthNumbers: Record<string, number> = {
    jan: 1, january: 1,
    feb: 2, february: 2,
    mar: 3, march: 3,
    apr: 4, april: 4,
    may: 5,
    jun: 6, june: 6,
    jul: 7, july: 7,
    aug: 8, august: 8,
    sep: 9, sept: 9, september: 9,
    oct: 10, october: 10,
    nov: 11, november: 11,
    dec: 12, december: 12,
};

function lastDayOfMonth(year: number, month: number): Date | null {
    if (month < 1 || month > 12)
        return null;
    const lastDay = new Date(year, month, 0).getDate();
    return parseYyyyMmDdToLocalDate(
        `${year}${String(month).padStart(2, '0')}${String(lastDay).padStart(2, '0')}`);
}

export function parsePeriodMemberNameToDate(name: string | null | undefined): Date | null {
    if (name == null)
        return null;
    const text = String(name).trim();
    const yearMonth = text.match(/^(\d{4})M(\d{1,2})$/i);
    if (yearMonth)
        return lastDayOfMonth(Number(yearMonth[1]), Number(yearMonth[2]));
    const named = text.match(/^(\d{4})[\s-]([A-Za-z]+)$/) ?? text.match(/^([A-Za-z]+)[\s-](\d{4})$/);
    if (!named)
        return null;
    // June, July, and Sept are 4 letters. A 4-character token is the year only when it is digits.
    const yearText = /^\d{4}$/.test(named[1]) ? named[1] : named[2];
    const monthText = yearText === named[1] ? named[2] : named[1];
    return lastDayOfMonth(Number(yearText), periodMonthNumbers[monthText.toLowerCase()] ?? 0);
}

type MemberDateSource = {
    memberName?: string | null;
    memberCaption?: string | null;
    properties?: { propertyName?: string | null; propertyValue?: string | null }[] | null;
};

export function getMemberLastDate(member: MemberDateSource | null | undefined): Date | null {
    if (member?.properties == null)
        return null;
    for (let i = 0; i < member.properties.length; i++) {
        const property = member.properties[i];
        if (property.propertyName == null || property.propertyName.toLowerCase() !== 'lastdate' || property.propertyValue == null)
            continue;
        const yyyymmdd = parseYyyyMmDdToLocalDate(property.propertyValue);
        if (yyyymmdd != null)
            return yyyymmdd;
        const millis = Date.parse(property.propertyValue);
        if (!Number.isNaN(millis)) {
            const parsed = new Date(millis);
            return parseYyyyMmDdToLocalDate(
                `${parsed.getFullYear()}${String(parsed.getMonth() + 1).padStart(2, '0')}${String(parsed.getDate()).padStart(2, '0')}`);
        }
    }
    return null;
}

export function resolveEffectiveDateFromTimeMember(
    timeMemberName: string | null | undefined,
    timeMembers: MemberDateSource[] | null | undefined,
): Date | null {
    if (timeMemberName == null || timeMemberName === '')
        return null;
    const members = timeMembers ?? [];
    const current = members.find(m => m.memberName === timeMemberName)
        ?? members.find(m => m.memberCaption === timeMemberName);
    return getMemberLastDate(current) ?? parsePeriodMemberNameToDate(timeMemberName);
}
